import { useMemo } from 'react';
import { realTraderScenarios } from '../data';

// Editorial first-person interviews. Cost figures are woven into what each
// trader actually "says" — the story carries the data, not the other way round.
const interviews = [
  {
    pair: 'EURUSD',
    role: 'Scalper',
    rhythm: '20 trades a day',
    accent: 'accent' as const,
    quote: [
      { t: 'I run ' },
      { t: 'twenty trades a day', hi: true },
      { t: ', every day. On my old spread-only account that markup quietly cost me ' },
      { t: '$5,760 a year', hi: true },
      { t: ' — I never saw it as a line item, it just lived inside every fill. Raw pricing plus commission dropped that to ' },
      { t: '$4,272', hi: true },
      { t: '. That\u2019s ' },
      { t: '$1,488 back', hi: true },
      { t: ' in my account, a ' },
      { t: '26% cut', hi: true },
      { t: ' for changing nothing but how I\u2019m charged. At my frequency, the spread markup is the enemy.' },
    ],
  },
  {
    pair: 'GBPUSD',
    role: 'Day Trader',
    rhythm: '4 trades a day, full lots',
    accent: 'primary' as const,
    quote: [
      { t: 'Four trades a day at a full lot adds up faster than people think. Spread-only was running me ' },
      { t: '$14,400 a year', hi: true },
      { t: ' in pure cost. The ECN account with its transparent commission brought that down to ' },
      { t: '$10,560', hi: true },
      { t: ' — I\u2019m keeping ' },
      { t: '$3,840 more', hi: true },
      { t: ' every single year. Nearly ' },
      { t: '27% cheaper', hi: true },
      { t: ' for the exact same trades. When you trade size, the pricing model isn\u2019t a detail — it\u2019s a salary.' },
    ],
  },
  {
    pair: 'USDJPY',
    role: 'Swing Trader',
    rhythm: '1 position a day, held',
    accent: 'secondary' as const,
    quote: [
      { t: 'I take maybe one position a day and hold it. Here\u2019s the honest truth nobody tells you: at my volume it barely matters. Spread-only costs me ' },
      { t: '$6,480 a year', hi: true },
      { t: ', ECN ' },
      { t: '$6,336', hi: true },
      { t: ' — a whole ' },
      { t: '$144 difference', hi: true },
      { t: '. That\u2019s ' },
      { t: '2.2%', hi: true },
      { t: '. I could obsess over it, but for a swing trader the spread-vs-commission fight is noise. My real costs live in the overnight swaps, not the entry.' },
    ],
  },
];

const accentText: Record<string, string> = {
  accent: 'text-accent-400',
  primary: 'text-primary-300',
  secondary: 'text-secondary-300',
};
const accentDot: Record<string, string> = {
  accent: 'bg-accent-500',
  primary: 'bg-primary-400',
  secondary: 'bg-secondary-400',
};
const accentGlow: Record<string, string> = {
  accent: 'bg-accent-500',
  primary: 'bg-primary-500',
  secondary: 'bg-secondary-500',
};

export default function RealTraderScenariosSection() {
  const data = useMemo(() => realTraderScenarios, []);

  return (
    <section className="relative w-full bg-foreground-950 overflow-hidden">
      {/* Ambient glows — same visual family as the hero */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/[0.03] via-transparent to-transparent" />
      <div className="absolute top-[-180px] right-[8%] w-[640px] h-[560px] rounded-full opacity-[0.10] blur-[160px] bg-accent-500" />
      <div className="absolute bottom-[-220px] left-[10%] w-[680px] h-[600px] rounded-full opacity-[0.08] blur-[170px] bg-primary-500" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />

      <div className="relative w-full max-w-main mx-auto px-4 md:px-6 py-12 md:py-20">

        {/* ── Editorial masthead ── */}
        <div className="mb-10 md:mb-14 max-w-[760px]">
          <h2 className="text-[32px] md:text-[46px] font-[750] leading-[1.08] text-white tracking-[-0.02em]">
            Three traders on what the pricing model
            {' '}
            <span className="italic text-accent-400">actually</span>
            {' '}
            costs them.
          </h2>
          <p className="mt-6 text-[15px] text-foreground-300 leading-relaxed max-w-[560px]">
            Same question, three very different answers — because the size of the gap depends entirely on how often, and how big, you trade.
          </p>
        </div>

        {/* ── The interviews ── */}
        <div className="space-y-14 md:space-y-24">
          {interviews.map((iv, idx) => {
            const t = data[idx];
            const name = t.name.split('\u2014')[0].trim();
            const isAlt = idx % 2 === 1;

            return (
              <article
                key={idx}
                className="group relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start"
              >
                {/* ── Byline column ── */}
                <div className={`lg:col-span-4 ${isAlt ? 'lg:order-2' : ''}`}>
                  <div className="lg:sticky lg:top-24">
                    {/* Index + rule */}
                    <div className="flex items-center gap-4 mb-6">
                      <span className={`text-[13px] font-bold tabular-nums tracking-[0.08em] ${accentText[iv.accent]}`}>
                        0{idx + 1}
                      </span>
                      <span className="h-px w-12 bg-white/[0.14]" />
                      <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-foreground-500">
                        {iv.pair}
                      </span>
                    </div>

                    {/* Name */}
                    <h3 className="text-[26px] md:text-[30px] font-[750] text-white tracking-[-0.015em] leading-[1.1]">
                      {name}
                    </h3>
                    <div className="mt-2 flex items-center gap-2.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${accentDot[iv.accent]}`} />
                      <span className="text-[14px] font-medium text-foreground-300">{iv.role}</span>
                    </div>

                    {/* Micro-bio line */}
                    <p className="mt-5 text-[13px] text-foreground-400 leading-relaxed max-w-[260px]">
                      {iv.rhythm}. {t.tradesPerDay * t.daysPerMonth} round-turns a month on {iv.pair} at {t.lotsPerTrade} lots a clip.
                    </p>
                  </div>
                </div>

                {/* ── Quote column ── */}
                <div className={`lg:col-span-8 relative ${isAlt ? 'lg:order-1' : ''}`}>
                  {/* Oversized quotation mark */}
                  <span
                    className={`absolute -top-8 md:-top-12 -left-1 md:-left-4 text-[110px] md:text-[150px] leading-none font-[750] select-none pointer-events-none opacity-[0.16] ${accentText[iv.accent]}`}
                    aria-hidden="true"
                  >
                    &ldquo;
                  </span>

                  {/* The quote itself */}
                  <blockquote className="relative pl-6 md:pl-10">
                    <p className="text-[21px] md:text-[27px] font-[350] italic leading-[1.5] md:leading-[1.5] text-foreground-100 tracking-[-0.01em]">
                      {iv.quote.map((seg, i) =>
                        seg.hi ? (
                          <span key={i} className={`not-italic font-[650] ${accentText[iv.accent]}`}>
                            {seg.t}
                          </span>
                        ) : (
                          <span key={i}>{seg.t}</span>
                        )
                      )}
                    </p>

                    {/* attribution footer */}
                    <footer className="mt-8 flex items-center gap-4 pl-1">
                      <span className={`h-[2px] w-10 rounded-full ${accentGlow[iv.accent]}`} />
                      <span className="text-[13px] text-foreground-400">
                        <span className="font-semibold text-foreground-200">{name}</span>
                        {' · '}
                        {t.verdict.split('\u2014')[0].trim()}
                      </span>
                    </footer>
                  </blockquote>
                </div>
              </article>
            );
          })}
        </div>

        {/* ── Editor's note (closing) ── */}
        <div className="mt-20 md:mt-28 pt-10 border-t border-white/[0.08] max-w-[820px]">
          <div className="flex items-start gap-5">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-foreground-500 pt-1.5 whitespace-nowrap">
              Editor&rsquo;s Note
            </span>
            <p className="text-[16px] md:text-[18px] text-foreground-300 leading-[1.75]">
              Read the three back to back and the pattern speaks for itself:{' '}
              <span className="text-white font-[650]">the more you trade, the louder ECN wins.</span>{' '}
              Marco and Sarah claw back roughly a quarter of their costs. Kenji, one trade a day, sees the two models all but converge. For the high-frequency crowd it&rsquo;s a financial decision — for everyone else, it&rsquo;s a preference.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}